import React, { useState } from 'react';
import { array, bool, func, object, string } from 'prop-types';
import classNames from 'classnames';

// Import configs and util modules
import { FormattedMessage } from '../../../../util/reactIntl';
import { LISTING_STATE_DRAFT } from '../../../../util/types';

// Import shared components
import { H3, ListingLink } from '../../../../components';

// Import modules from this directory
import EditListingPhotosForm from './EditListingPhotosForm';
import css from './EditListingPhotosPanel.module.css';

const getInitialValues = ({ props, listingImagesUrl = [], heroImageId, otherImages = [], listingVideo = {} }) => {
  const { images } = props;
  const heroImg = !!heroImageId && listingImagesUrl.map(listImg => listImg.public_id).includes(heroImageId)
    ? heroImageId
    : listingImagesUrl && listingImagesUrl.length
      ? listingImagesUrl[0]?.public_id
      : null;
  return { images, listingImagesUrl, heroImg, otherImages, listingVideo };
};

const EditListingPhotosPanel = props => {
  const { listingImagesUrl = [], heroImageId, listingVideo, otherImages = [], anyDamage, productType, mainProductType } = props?.listing?.attributes?.publicData || {};

  const [saveAsDraft, setSaveAsDraft] = useState(false);
  const [deleteImageIds, setDeleteImageIds] = useState([]);
  const [isVideoDeleted, setIsVideoDeleted] = useState(false);
  const [uploadImages, setUploadImages] = useState(
    listingImagesUrl?.length > 0 ? listingImagesUrl : []
  );
  const [uploadOtherImages, setUploadOtherImages] = useState(otherImages?.length > 0 ? otherImages : []);
  const [uploadVideo, setUploadVideo] = useState(listingVideo ? listingVideo : {});

  const {
    className,
    rootClassName,
    errors,
    disabled,
    ready,
    listing,
    onImageUpload,
    submitButtonText,
    panelUpdated,
    updateInProgress,
    onSubmit,
    onRemoveImage,
    listingImageConfig,
    onUploadImagesToCloudinary,
    onUploadVideosToCloudinary,
    uploadVideoInProgress,
    uploadVideoError,
    uploadedVideo,
    onDeleteFromCloudinary,
    uploadImagesInProgress,
    uploadedImage,
    uploadImageError,
    history,
    onDeleteMultipleImages
  } = props;

  const rootClass = rootClassName || css.root;
  const classes = classNames(rootClass, className);
  const isPublished = listing?.id && listing?.attributes?.state !== LISTING_STATE_DRAFT;

  return (
    <div className={classes}>
      <div className={css.container}>

        <H3 as="h1">
          {isPublished ? (
            <FormattedMessage
              id="EditListingPhotosPanel.title"
              values={{ listingTitle: <ListingLink listing={listing} />, lineBreak: <br /> }}
            />
          ) : (
            <FormattedMessage
              id="EditListingPhotosPanel.createListingTitle"
              values={{ lineBreak: <br /> }}
            />
          )}
        </H3>
        <p className={css.tip}>
          <FormattedMessage id="EditListingPhotosForm.addImagesDesclaimer" />
        </p>
        <EditListingPhotosForm
          className={css.form}
          disabled={disabled}
          ready={ready}
          fetchErrors={errors}
          initialValues={getInitialValues({ props, listingImagesUrl: uploadImages, heroImageId, otherImages: uploadOtherImages, listingVideo: uploadVideo })}
          onImageUpload={onImageUpload}
          listingImagesUrl={listingImagesUrl}
          onSubmit={values => {
            const {
              addImage,
              heroImg,
              listingImagesUrl,
              otherImages,
              listingVideo,
              ...updateValues
            } = values;

            // Determine hero image ID
            const heroImageId =
              heroImg ||
              listingImagesUrl?.[0]?.public_id ||
              uploadImages?.[0]?.public_id ||
              null;

            // Construct the listing video object
            const listingVideoObj = listingVideo?.url
              ? {
                url: listingVideo.url,
                public_id: listingVideo.public_id,
                resource_type: listingVideo.resource_type,
              }
              : uploadVideo?.url
                ? {
                  url: uploadVideo.url,
                  public_id: uploadVideo.public_id,
                  resource_type: uploadVideo.resource_type,
                }
                : null;

            // Compose publicData
            updateValues.publicData = {
              listingImagesUrl: (uploadImages?.length > 0) ? uploadImages : listingImagesUrl || [],
              heroImageId,
              updatedAt: Math.floor(Date.now() / 1000),
              listingVideo: listingVideoObj,
              otherImages: (uploadOtherImages?.length > 0) ? uploadOtherImages : (otherImages.length > 0) ? otherImages : null,
              version: mainProductType ? 2 : null,
            };

            // Submit the data and handle post-submission cleanups
            onSubmit(updateValues).then(() => {
              const promises = [];

              if (deleteImageIds.length > 0) {
                promises.push(onDeleteMultipleImages({ deleteImageIds }));
              }

              if (listingVideo?.url && isVideoDeleted) {
                promises.push(onDeleteFromCloudinary(listingVideo));
              }

              return Promise.all(promises);
            });
          }}
          onRemoveImage={onRemoveImage}
          saveActionMsg={submitButtonText}
          updated={panelUpdated}
          updateInProgress={updateInProgress}
          listingImageConfig={listingImageConfig}
          onUploadImagesToCloudinary={onUploadImagesToCloudinary}
          listingId={listing?.id?.uuid}
          onUploadVideosToCloudinary={onUploadVideosToCloudinary}
          uploadVideoInProgress={uploadVideoInProgress}
          uploadVideoError={uploadVideoError}
          uploadedVideo={uploadedVideo}
          onDeleteFromCloudinary={onDeleteFromCloudinary}
          uploadImagesInProgress={uploadImagesInProgress}
          uploadedImage={uploadedImage}
          uploadImageError={uploadImageError}
          onSaveDraft={onSubmit}
          setSaveAsDraft={setSaveAsDraft}
          saveAsDraft={saveAsDraft}
          history={history}
          anyDamage={anyDamage}
          heroImageId={heroImageId}
          setDeleteImageIds={setDeleteImageIds}
          savedListingVideo={listingVideo}
          productType={productType}
          setUploadImages={setUploadImages}
          uploadImages={uploadImages}
          uploadOtherImages={uploadOtherImages}
          setUploadOtherImages={setUploadOtherImages}
          setUploadVideo={setUploadVideo}
          uploadVideo={uploadVideo}
          setIsVideoDeleted={setIsVideoDeleted}
          mainProductType={mainProductType}
        />
      </div>
    </div>
  );
};

EditListingPhotosPanel.defaultProps = {
  className: null,
  rootClassName: null,
  errors: null,
  images: [],
  listing: null,
};

EditListingPhotosPanel.propTypes = {
  className: string,
  rootClassName: string,
  errors: object,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  images: array,

  // We cannot use propTypes.listing since the listing might be a draft.
  listing: object,

  onImageUpload: func.isRequired,
  onSubmit: func.isRequired,
  submitButtonText: string.isRequired,
  panelUpdated: bool.isRequired,
  updateInProgress: bool.isRequired,
  onRemoveImage: func.isRequired,
  listingImageConfig: object.isRequired,
};

export default EditListingPhotosPanel;
